import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    FaPlay,
    FaPause,
    FaVolumeUp,
    FaVolumeMute,
    FaTimes,
    FaExpand,
    FaCompress,
    FaHeart,
    FaRegHeart,
    FaShareAlt,
    FaWifi,
    FaList,
    FaCreditCard,
    FaLock
} from 'react-icons/fa';
import { MdSkipNext } from 'react-icons/md';
import { useMyContext } from '../../../../components/Layout/CommonProvider';
import { getRadioStationList } from '../../../../components/Api/RadioStationApi';
import { playRadioStationAction, toggleEPGModalAction } from '../../../../Redux/Actions/RadioStationAction';
import { getItem } from '../../../../Utils/localStorageUtils';
import RadioEPGModal from './RadioEPGModal';
import NextStationPanel from './NextStationPanel';
import './FloatingRadioPlayer.css';

const FloatingRadioPlayer = ({ station, onClose }) => {
    const { navigateTranslateChecker } = useMyContext();
    const dispatch = useDispatch();
    const { showEPGModal } = useSelector((state) => state.get_radio_Reducers?.player || {});

    // Audio element reference
    const audioRef = useRef(null);

    // Player states
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(() => {
        const savedVolume = localStorage.getItem('audio_volume');
        return savedVolume ? parseFloat(savedVolume) : 0.7;
    });
    const [isMuted, setIsMuted] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Autoplay restriction handling
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [showAutoplayWarning, setShowAutoplayWarning] = useState(false);
    const [autoplayBlocked, setAutoplayBlocked] = useState(false);

    // Station data states
    const [isLiked, setIsLiked] = useState(false);
    const [currentProgram, setCurrentProgram] = useState(null);
    const [availableStations, setAvailableStations] = useState([]);
    const [currentStationIndex, setCurrentStationIndex] = useState(0);

    // Modal states
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [showNextPanel, setShowNextPanel] = useState(false);

    // Audio loading states
    const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
    const [availableUrls, setAvailableUrls] = useState([]);
    const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
    const [embedUrl, setEmbedUrl] = useState(null);
    const [playerType, setPlayerType] = useState('audio'); // 'audio' or 'embed'
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('online');

    /**
     * Extract URL from iframe HTML string
     */
    const extractIframeUrl = useCallback((htmlString) => {
        if (!htmlString || typeof htmlString !== 'string') return null;

        // Match iframe src attribute
        const iframeMatch = htmlString.match(/<iframe[^>]*src=["']([^"']*)["'][^>]*>/i);
        if (iframeMatch && iframeMatch[1]) {
            console.log('📺 Extracted iframe URL:', iframeMatch[1]);
            return iframeMatch[1];
        }

        return null;
    }, []);

    /**
     * Get all available audio sources with priority order
     */
    const getAudioSources = useCallback((stationData) => {
        if (!stationData) {
            console.log('🚫 No station data provided');
            return { audioUrls: [], embedUrl: null };
        }

        console.log('🎵 Processing audio sources for station:', stationData.title);

        const audioUrls = [];
        let embedUrl = null;

        // Helper function to check if content is iframe HTML
        const isIframeHtml = (content) => {
            if (!content) return false;
            return content.includes('<iframe') && content.includes('src=');
        };

        // Helper function to check if URL is an embed URL
        const isEmbedUrl = (url) => {
            if (!url) return false;
            const lowerUrl = url.toLowerCase();
            const embedPatterns = [
                'youtube.com', 'youtu.be', 'vimeo.com', 'facebook.com',
                'instagram.com', 'soundcloud.com', 'dailymotion.com', 'twitch.tv',
                'iono.fm', 'iframe.' // Support iono.fm and other iframe embeds
            ];
            return embedPatterns.some(pattern => lowerUrl.includes(pattern));
        };

        // Helper function to check if URL is a direct audio stream
        const isDirectAudioUrl = (url) => {
            if (!url) return false;
            const lowerUrl = url.toLowerCase();

            // Check for direct audio file extensions
            if (lowerUrl.includes('.mp3') || lowerUrl.includes('.aac') ||
                lowerUrl.includes('.m3u8') || lowerUrl.includes('.wav') ||
                lowerUrl.includes('.ogg') || lowerUrl.includes('.m3u')) {
                return true;
            }

            // Check for streaming domains and patterns
            const streamingPatterns = [
                'stream.', 'radio.', 'audio.', 'listen.', 'icecast', 'shoutcast',
                '/stream', '/radio', '/live', ':8000', ':8080', ':9000'
            ];

            return streamingPatterns.some(pattern => lowerUrl.includes(pattern));
        };

        // Priority order for audio sources
        const sources = [
            { key: 'radio_url', url: stationData.radio_url, label: 'Radio URL' },
            { key: 'mp3_audio_upload', url: stationData.mp3_audio_upload, label: 'MP3 Audio Upload' },
            { key: 'aac_audio_upload', url: stationData.aac_audio_upload, label: 'AAC Audio Upload' },
            { key: 'mp3_url', url: stationData.mp3_url, label: 'MP3 URL' },
            { key: 'aac_audio_url', url: stationData.aac_audio_url, label: 'AAC Audio URL' },
            { key: 'embed_url', url: stationData.embed_url, label: 'Embed URL' },
            // Additional fallbacks from your existing data
            { key: 'mp4_url', url: stationData.mp4_url, label: 'MP4 URL' },
            { key: 'm3u8_url', url: stationData.m3u8_url, label: 'M3U8 URL' },
            { key: 'acc_audio_file', url: stationData.acc_audio_file, label: 'ACC Audio File' }
        ];

        // Process each source with priority order
        sources.forEach(source => {
            if (source.url && typeof source.url === 'string' && source.url.trim()) {
                const cleanUrl = source.url.trim();

                console.log(`🔍 Processing source: ${source.label} = ${cleanUrl.substring(0, 100)}...`);

                // Check if it's iframe HTML content
                if (isIframeHtml(cleanUrl)) {
                    const extractedUrl = extractIframeUrl(cleanUrl);
                    if (extractedUrl) {
                        if (!embedUrl) {
                            embedUrl = extractedUrl;
                            console.log('📺 Found iframe embed:', source.label, '->', extractedUrl);
                        }
                    }
                } else if (isEmbedUrl(cleanUrl)) {
                    if (!embedUrl) { // Only set the first embed URL found
                        embedUrl = cleanUrl;
                        console.log('📺 Found embed URL:', source.label, '->', cleanUrl);
                    }
                } else if (isDirectAudioUrl(cleanUrl) || !isEmbedUrl(cleanUrl)) {
                    // Add to audio URLs if it's not already added
                    if (!audioUrls.includes(cleanUrl)) {
                        audioUrls.push(cleanUrl);
                        console.log('✅ Added audio source:', source.label, '->', cleanUrl);
                    }
                }
            }
        });

        // IMPORTANT: Always add mp4_url as audio fallback even if embed is found
        if (stationData.mp4_url && !audioUrls.includes(stationData.mp4_url)) {
            audioUrls.push(stationData.mp4_url);
            console.log('🎵 Added mp4_url as audio fallback:', stationData.mp4_url);
        }

        console.log('📦 Final audio sources:', {
            audioUrls: audioUrls.length,
            hasEmbed: !!embedUrl,
            firstAudioUrl: audioUrls[0],
            embedUrl
        });

        return { audioUrls, embedUrl };
    }, [extractIframeUrl]);

    /**
     * Check if user has access to the station
     */
    const hasStationAccess = useCallback((stationData) => {
        if (!stationData) return { hasAccess: false, reason: 'No station data' };

        const userRole = parseInt(getItem('role'), 10) || 9; // Default guest role
        const userId = getItem('user_id');
        const isLoggedIn = !!userId;

        // Check access type
        const accessType = stationData.access || stationData.access_type || 'free';
        const isPaid = accessType === 'ppv' || accessType === 'paid' || accessType === 'premium';

        if (!isPaid || accessType === 'free' || accessType === 'guest') {
            return { hasAccess: true, accessType: 'free' };
        }

        if (!isLoggedIn) {
            return {
                hasAccess: false,
                reason: 'login_required',
                accessType: accessType
            };
        }

        // Check user subscription level (adjust based on your role system)
        const hasValidSubscription = userRole === 1 || userRole === 2; // Premium or Admin

        if (isPaid && !hasValidSubscription) {
            return {
                hasAccess: false,
                reason: 'subscription_required',
                accessType: accessType
            };
        }

        return { hasAccess: true, accessType: accessType };
    }, []);

    // Ref to track if component is mounted to prevent memory leaks
    const isMountedRef = useRef(true);

    // Ref to track current loading operation
    const loadingOperationRef = useRef(null);

    /**
     * Load audio with multiple URL fallbacks - prevents AbortError
     */
    const loadAudioWithFallbacks = useCallback(async (audioUrls, urlIndex = 0) => {
        console.log('🔧 loadAudioWithFallbacks called:', {
            audioUrls: audioUrls?.length ? audioUrls : 'No URLs',
            urlIndex,
            totalUrls: audioUrls?.length || 0
        });
        
        if (!audioUrls || audioUrls.length === 0) {
            console.log('❌ No audio URLs provided');
            setError('No audio sources available');
            return false;
        }

        if (urlIndex >= audioUrls.length) {
            console.log('❌ All audio URLs failed');
            setError('All audio sources failed to load');
            return false;
        }

        const url = audioUrls[urlIndex];
        console.log(`🚀 Trying audio URL ${urlIndex + 1}/${audioUrls.length}:`, url);

        const audio = audioRef.current;
        if (!audio || !isMountedRef.current) {
            console.log('❌ Missing audio element or component unmounted');
            return false;
        }

        // Cancel any existing loading operation
        if (loadingOperationRef.current) {
            console.log('🖾 Cancelling existing loading operation');
            loadingOperationRef.current.cancel = true;
        }

        // Create new loading operation
        const currentOperation = { cancel: false };
        loadingOperationRef.current = currentOperation;

        console.log('💾 Setting audio source to:', url);
        console.log('🔍 Current audio element state before setting source:', {
            currentSrc: audio.src,
            readyState: audio.readyState,
            networkState: audio.networkState
        });
        
        setCurrentAudioUrl(url);
        setCurrentUrlIndex(urlIndex);
        setIsLoading(true);
        setError(null);
        setPlayerType('audio');

        try {
            // Stop any current playback and clear source to prevent conflicts
            if (!audio.paused) {
                audio.pause();
            }
            
            // Clear current source to prevent AbortError
            if (audio.src) {
                audio.removeAttribute('src');
                audio.load(); // Reset the audio element
            }

            // Wait a moment for cleanup to complete
            await new Promise(resolve => setTimeout(resolve, 150));

            // Check if operation was cancelled
            if (currentOperation.cancel || !isMountedRef.current) {
                return false;
            }

            // Set new source
            console.log('💾 Setting audio src to:', url);
            audio.src = url;
            audio.volume = isMuted ? 0 : volume;
            audio.preload = 'metadata';
            audio.load(); // Force load the new source

            // Wait for audio to be ready
            return new Promise((resolve) => {
                const cleanup = () => {
                    audio.removeEventListener('canplaythrough', onSuccess);
                    audio.removeEventListener('error', onError);
                    audio.removeEventListener('loadeddata', onSuccess);
                    clearTimeout(timeoutId);

                    if (isMountedRef.current) {
                        setIsLoading(false);
                    }
                };

                const onSuccess = () => {
                    if (currentOperation.cancel || !isMountedRef.current) {
                        cleanup();
                        resolve(false);
                        return;
                    }

                    console.log('✅ Audio loaded successfully:', url);
                    cleanup();
                    if (isMountedRef.current) {
                        setError(null);
                    }
                    resolve(true);
                };

                const onError = (e) => {
                    if (currentOperation.cancel || !isMountedRef.current) {
                        cleanup();
                        resolve(false);
                        return;
                    }

                    console.log(`❌ Audio failed (${urlIndex + 1}/${audioUrls.length}):`, url, e.target?.error);
                    cleanup();

                    // Try next URL after delay
                    setTimeout(() => {
                        if (isMountedRef.current) {
                            loadAudioWithFallbacks(audioUrls, urlIndex + 1).then(resolve);
                        }
                    }, 1000);
                };

                const onTimeout = () => {
                    if (currentOperation.cancel || !isMountedRef.current) {
                        cleanup();
                        resolve(false);
                        return;
                    }

                    console.log(`⏱️ Audio timeout (${urlIndex + 1}/${audioUrls.length}):`, url);
                    cleanup();

                    // Try next URL after delay
                    setTimeout(() => {
                        if (isMountedRef.current) {
                            loadAudioWithFallbacks(audioUrls, urlIndex + 1).then(resolve);
                        }
                    }, 1000);
                };

                // Add event listeners
                audio.addEventListener('canplaythrough', onSuccess, { once: true });
                audio.addEventListener('loadeddata', onSuccess, { once: true });
                audio.addEventListener('error', onError, { once: true });

                // Set timeout
                const timeoutId = setTimeout(onTimeout, 10000); // 10 second timeout

                // Start loading
                if (!currentOperation.cancel) {
                    audio.load();
                }
            });

        } catch (error) {
            console.log('❌ Exception in loadAudioWithFallbacks:', error);
            if (isMountedRef.current) {
                setIsLoading(false);
                // Try next URL on exception
                if (urlIndex + 1 < audioUrls.length) {
                    return await loadAudioWithFallbacks(audioUrls, urlIndex + 1);
                } else {
                    setError('Failed to initialize audio stream');
                }
            }
            return false;
        }
    }, [volume, isMuted]);

    /**
     * Setup embed player (for YouTube, etc.)
     */
    const setupEmbedPlayer = useCallback((embedUrl) => {
        console.log('📺 Setting up embed player for:', embedUrl);
        setEmbedUrl(embedUrl);
        setPlayerType('embed');
        setCurrentAudioUrl(null);
        setIsLoading(false);
        setError(null);
        setIframeLoaded(false);
        setIframeError(false);

        // Don't assume embed player auto-starts - let user control playback
        setIsPlaying(false);
        setShowAutoplayWarning(true);

        // Set a timeout for iframe loading
        const timeoutId = setTimeout(() => {
            if (!iframeLoaded && !iframeError) {
                console.log('⏱️ Iframe loading timeout');
                setIframeError(true);
                //  toast.warning('📺 Iframe player is taking too long to load');
            }
        }, 15000); // 15 second timeout

        // Clear timeout when iframe loads or errors
        const cleanup = () => clearTimeout(timeoutId);
        return cleanup;

    }, [iframeLoaded, iframeError]);

    /**
     * Initialize station audio
     */
    useEffect(() => {
        const initializeAudio = async () => {
            if (!station || !audioRef.current) return;

            console.log('🎵 Initializing station:', station.title);

            // Check access first
            const accessInfo = hasStationAccess(station);
            if (!accessInfo.hasAccess) {
                console.log('❌ Access denied:', accessInfo.reason);
                if (accessInfo.reason === 'subscription_required') {
                    setShowAccessModal(true);
                } else if (accessInfo.reason === 'login_required') {
                    setError('Please log in to access this station');
                } else {
                    setError('Access denied to this station');
                }
                return;
            }

            // Get all audio sources
            const { audioUrls, embedUrl: stationEmbedUrl } = getAudioSources(station);

            console.log('🎯 Station initialization - Audio sources analysis:', {
                hasEmbedUrl: !!stationEmbedUrl,
                embedUrl: stationEmbedUrl,
                audioUrlsCount: audioUrls.length,
                audioUrls: audioUrls
            });
            
            if (stationEmbedUrl) {
                // Prioritize embed player for iframe content
                console.log('📺 Embed URL found, using embed player:', stationEmbedUrl);
                setupEmbedPlayer(stationEmbedUrl);
            } else if (audioUrls.length > 0) {
                // Try audio URLs only if no embed URL
                console.log('✅ Found', audioUrls.length, 'audio URLs, trying fallback loading');
                console.log('🔍 Audio URLs list:', audioUrls);
                setAvailableUrls(audioUrls);
                await loadAudioWithFallbacks(audioUrls, 0);
            } else {
                // No audio sources found
                console.log('❌ No valid audio sources found');
                setError('No playable audio sources found for this station');
            }
        };

        initializeAudio();
    }, [station, hasStationAccess, getAudioSources, loadAudioWithFallbacks, setupEmbedPlayer]);

    /**
     * Audio event listeners
     */
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => {
            setIsPlaying(true);
            setIsLoading(false);
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        const handleWaiting = () => {
            setIsLoading(true);
        };

        const handlePlaying = () => {
            setIsLoading(false);
        };

        const handleError = (e) => {
            setIsLoading(false);
            setIsPlaying(false);
            setError('Audio playback failed');
        };

        // Add event listeners
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('waiting', handleWaiting);
        audio.addEventListener('playing', handlePlaying);
        audio.addEventListener('error', handleError);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('playing', handlePlaying);
            audio.removeEventListener('error', handleError);
        };
    }, []);
    /**
     * Toggle play/pause - Fixed to handle autoplay restrictions
     */
    const togglePlayPause = useCallback(async () => {
        console.log('🎵 togglePlayPause called - User clicked play button!');
        
        const audio = audioRef.current;
        if (!audio || !isMountedRef.current) {
            console.log('❌ Missing audio element or component unmounted');
            return;
        }

        console.log('🔍 Current player state:', {
            isLoading,
            isPlaying,
            currentAudioUrl,
            playerType,
            hasUserInteracted,
            autoplayBlocked,
            showAutoplayWarning,
            audioElement: {
                src: audio.src,
                readyState: audio.readyState,
                networkState: audio.networkState,
                paused: audio.paused,
                currentTime: audio.currentTime,
                duration: audio.duration
            }
        });

        if (isLoading) {
            console.log('⏳ Audio is still loading, please wait...');
            //  toast.info('Audio is loading, please wait...');
            return;
        }

        if (!currentAudioUrl && playerType !== 'embed') {
            console.log('❌ No audio source available', { currentAudioUrl, playerType });
            setError('No audio source available');
            return;
        }

        // Mark that user has interacted - this allows future autoplay
        if (!hasUserInteracted) {
            console.log('✅ User interaction detected - enabling audio playback');
            setHasUserInteracted(true);
            setShowAutoplayWarning(false);
            setAutoplayBlocked(false);
        }

        // Check access again
        const accessInfo = hasStationAccess(station);
        if (!accessInfo.hasAccess) {
            console.log('🔒 Access denied:', accessInfo);
            if (accessInfo.reason === 'subscription_required') {
                setShowAccessModal(true);
            }
            return;
        }

        try {
            if (playerType === 'embed') {
                // Handle embed player (iframe)
                console.log('📺 Embed player mode - checking if we have audio fallback available...');
                
                if (isPlaying) {
                    // For embed players, we simulate pause by updating state
                    setIsPlaying(false);
                    console.log('📺 Iframe player paused (state only)');
                } else {
                    // For embed players, try to start but also offer audio fallback
                    setIsPlaying(true);
                    setShowAutoplayWarning(false);
                    setAutoplayBlocked(false);
                    console.log('📺 Iframe player activated - use embedded controls');
                    
                    // Show option to try audio fallback if user clicks and nothing happens
                    setTimeout(() => {
                        if (isPlaying && availableUrls && availableUrls.length > 0) {
                            console.log('🔄 Embed player started - audio fallback available if needed');
                            // Don't automatically switch - let user use embedded controls
                            // But make fallback available via error handling or manual switch
                        }
                    }, 3000);
                }
                return;
            }

            // Handle audio player
            if (isPlaying) {
                console.log('⏸️ Pausing audio playback');
                // Simple pause - no AbortError risk
                audio.pause();
            } else {
                console.log('▶️ Starting audio playbook...');
                console.log('🔍 Audio element details:', {
                    currentSrc: audio.src,
                    targetSrc: currentAudioUrl,
                    readyState: audio.readyState,
                    networkState: audio.networkState,
                    paused: audio.paused,
                    volume: audio.volume,
                    muted: audio.muted
                });
                
                // Cancel any existing play operations to prevent AbortError
                if (!audio.paused) {
                    console.log('⏸️ Pausing current playback to prevent AbortError');
                    audio.pause();
                    // Wait for pause to complete
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
                
                // Ensure source is correct
                if (audio.src !== currentAudioUrl) {
                    console.log('🔄 Setting new audio source:', currentAudioUrl);
                    audio.src = currentAudioUrl;
                    audio.load(); // Force reload
                    console.log('✅ Audio source updated and reloaded');
                }
                
                // Wait for audio to be ready before playing
                console.log('⏳ Waiting for audio to be ready...');
                const waitForReady = () => new Promise((resolve, reject) => {
                    const checkReady = () => {
                        console.log(`🔍 Audio readyState: ${audio.readyState} (need >= 3 for safe play)`);
                        if (audio.readyState >= 2) { // HAVE_CURRENT_DATA or better
                            console.log('✅ Audio is ready for playbook!');
                            resolve();
                        } else if (audio.error) {
                            console.log('❌ Audio loading error:', audio.error);
                            reject(new Error(`Audio loading failed: ${audio.error.message}`));
                        } else {
                            // Wait a bit more
                            setTimeout(checkReady, 100);
                        }
                    };
                    
                    // Add event listeners for faster detection
                    const onCanPlay = () => {
                        console.log('✅ Audio canplay event fired');
                        audio.removeEventListener('canplay', onCanPlay);
                        audio.removeEventListener('error', onError);
                        resolve();
                    };
                    
                    const onError = (e) => {
                        console.log('❌ Audio error event:', e);
                        audio.removeEventListener('canplay', onCanPlay);
                        audio.removeEventListener('error', onError);
                        reject(new Error('Audio failed to load'));
                    };
                    
                    audio.addEventListener('canplay', onCanPlay, { once: true });
                    audio.addEventListener('error', onError, { once: true });
                    
                    // Start checking
                    checkReady();
                    
                    // Timeout after 10 seconds
                    setTimeout(() => {
                        audio.removeEventListener('canplay', onCanPlay);
                        audio.removeEventListener('error', onError);
                        reject(new Error('Audio loading timeout'));
                    }, 10000);
                });
                
                try {
                    await waitForReady();
                    
                    console.log('🚀 Audio is ready, attempting to play...');
                    const playPromise = audio.play();
                    if (playPromise !== undefined) {
                        console.log('⏳ Waiting for play promise...');
                        await playPromise;
                        console.log('✅ Audio play promise resolved successfully!');
                    } else {
                        console.log('ℹ️ Audio.play() returned undefined (older browser)');
                    }
                } catch (waitError) {
                    console.log('❌ Error waiting for audio ready:', waitError);
                    throw waitError;
                }
            }
        } catch (err) {
            console.log('❌ Audio play error caught:', {
                name: err.name,
                message: err.message,
                code: err.code,
                stack: err.stack
            });
            
            if (!isMountedRef.current) return;

            let errorMsg = 'Could not play audio';
            if (err.name === 'NotAllowedError') {
                console.log('🔒 Browser autoplay policy blocked audio - this should not happen after user click!');
                errorMsg = 'Click the play button to start audio (browser autoplay policy)';
                setAutoplayBlocked(true);
                setShowAutoplayWarning(true);
                setIsPlaying(false);
                // Don't show toast error for autoplay - it's expected behavior
                console.log('🔒 Autoplay blocked by browser - user interaction required');
                return; // Don't treat this as an error
            } else if (err.name === 'NotSupportedError') {
                errorMsg = 'Audio format not supported';
            } else if (err.name === 'AbortError') {
                // For AbortError, try next audio URL or fallback to embed
                errorMsg = 'Stream interrupted. Trying alternatives...';

                // Try next audio URL if available
                if (availableUrls && currentUrlIndex + 1 < availableUrls.length) {
                    setTimeout(() => {
                        if (isMountedRef.current) {
                            loadAudioWithFallbacks(availableUrls, currentUrlIndex + 1);
                        }
                    }, 1000);
                    return; // Don't show error, just retry
                }

                // If no more audio URLs, try embed as fallback
                const { embedUrl: stationEmbedUrl } = getAudioSources(station);
                if (stationEmbedUrl) {
                    console.log('🔄 AbortError: Falling back to embed player');
                    setupEmbedPlayer(stationEmbedUrl);
                    return;
                }
            } else if (err.name === 'NetworkError') {
                errorMsg = 'Network error. Check your connection.';
            }

            setError(errorMsg);
            //  toast.error(errorMsg);
        }
    }, [isPlaying, isLoading, currentAudioUrl, playerType, hasStationAccess, station, availableUrls, currentUrlIndex, loadAudioWithFallbacks, getAudioSources, setupEmbedPlayer]);

    /**
     * Volume control
     */
    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        localStorage.setItem('audio_volume', newVolume.toString());

        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : Math.min(newVolume, 1.0);
        }

        if (newVolume > 0 && isMuted) {
            setIsMuted(false);
        }
    };

    /**
     * Toggle mute
     */
    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isMuted) {
            audio.volume = volume;
            setIsMuted(false);
        } else {
            audio.volume = 0;
            setIsMuted(true);
        }
    };

    /**
     * Other UI handlers
     */
    const toggleExpanded = () => setIsExpanded(!isExpanded);
    const handleLikeToggle = () => setIsLiked(!isLiked);

    const handleShare = () => {
        const url = window.location.origin + navigateTranslateChecker(`/music/radio/${station.slug}`);

        if (navigator.share) {
            navigator.share({
                title: station.title,
                text: station.description,
                url: url
            });
        } else {
            navigator.clipboard.writeText(url);
            //  toast.success('Station link copied to clipboard!');
        }
    };

    const handleEPGClick = () => {
        dispatch(toggleEPGModalAction(true));
    };

    const handleNextButtonClick = () => {
        setShowNextPanel(true);
    };

    const handleAccessDenied = () => {
        window.location.href = '/plan';
    };

    // Load available stations for Next functionality
    useEffect(() => {
        const loadStations = async () => {
            try {
                const userRole = parseInt(getItem('role'), 10) || 9;
                const userId = parseInt(getItem('user_id'), 10) || null;

                const response = await getRadioStationList({
                    Login_device_type: 'web',
                    Login_user_type: userRole,
                    Login_user_id: userId,
                    limit: 50
                });

                if (response.data?.status && response.data?.data) {
                    setAvailableStations(response.data.data);
                    const currentIndex = response.data.data.findIndex(s => s.id === station?.id);
                    setCurrentStationIndex(currentIndex >= 0 ? currentIndex : 0);
                }
            } catch (error) {
                // Silently handle error - stations list will remain empty
            }
        };

        if (station?.id) {
            loadStations();
        }
    }, [station?.id]);

    // Get current program info
    useEffect(() => {
        if (station?.radio_station_programs?.length > 0) {
            const now = new Date();
            const currentTime = now.toTimeString().slice(0, 8);

            const current = station.radio_station_programs.find(program => {
                return currentTime >= program.start_time && currentTime <= program.end_time;
            });

            setCurrentProgram(current || station.radio_station_programs[0]);
        }
    }, [station]);

    const handleNextStationFromPanel = async (selectedStation) => {
        // Check access
        const accessInfo = hasStationAccess(selectedStation);
        if (!accessInfo.hasAccess) {
            setShowAccessModal(true);
            return;
        }

        try {
            dispatch(playRadioStationAction(selectedStation));
            //  toast.success(`Switched to ${selectedStation.title}`);
            setShowNextPanel(false);
        } catch (error) {
            //  toast.error('Failed to switch station');
        }
    };

    // Monitor network connectivity for better error handling
    useEffect(() => {
        const updateConnectionStatus = () => {
            const status = navigator.onLine ? 'online' : 'offline';
            setConnectionStatus(status);
            if (status === 'offline') {
                //  toast.warning('📶 You are offline. Some features may not work.');
            } else {
                console.log('📶 Connection restored');
            }
        };

        // Initial check
        updateConnectionStatus();

        // Add event listeners
        window.addEventListener('online', updateConnectionStatus);
        window.addEventListener('offline', updateConnectionStatus);

        return () => {
            window.removeEventListener('online', updateConnectionStatus);
            window.removeEventListener('offline', updateConnectionStatus);
        };
    }, []);

    // Detect autoplay policy on mount
    useEffect(() => {
        const detectAutoplayPolicy = async () => {
            // Check if browser supports autoplay policy detection
            if ('getAutoplayPolicy' in navigator) {
                try {
                    const policy = navigator.getAutoplayPolicy('mediaelement');
                    if (policy === 'disallowed') {
                        setShowAutoplayWarning(true);
                        console.log('🔒 Browser autoplay policy: disallowed');
                    }
                } catch (e) {
                    // Policy detection not supported, assume modern browser restrictions
                    setShowAutoplayWarning(true);
                }
            } else {
                // For browsers without policy API, show warning by default for safety
                setShowAutoplayWarning(true);
                console.log('🔒 Browser autoplay policy detection not supported - showing warning');
            }
        };

        detectAutoplayPolicy();
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (loadingOperationRef.current) {
                loadingOperationRef.current.cancel = true;
            }
            const audio = audioRef.current;
            if (audio) {
                audio.pause();
                audio.src = '';
            }
        };
    }, []);

    if (!station) return null;

    return (
        <>
            {/* Audio Element */}
            <audio
                ref={audioRef}
                preload="metadata"
                controls={false}
                autoPlay={false}
                muted={false}
                playsInline={true}
                style={{ display: playerType === 'audio' ? 'block' : 'none' }}
            />

            {/* Embed Player - Always Visible */}
            {playerType === 'embed' && embedUrl && (
                <div
                    className="embed-player-container"
                    id="radio-iframe-player"
                    style={{
                        display: 'block',
                        position: isExpanded ? 'relative' : 'fixed',
                        bottom: isExpanded ? 'auto' : '100px',
                        right: isExpanded ? 'auto' : '20px',
                        left: isExpanded ? 'auto' : 'auto',
                        width: isExpanded ? '100%' : 'min(350px, 90vw)',
                        height: isExpanded ? 'auto' : 'min(220px, 40vh)',
                        maxWidth: isExpanded ? '100%' : '350px',
                        maxHeight: isExpanded ? '600px' : '220px',
                        zIndex: 10000,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: isExpanded ? 'none' : '0 8px 25px rgba(0,0,0,0.4)',
                        border: isExpanded ? 'none' : '2px solid #007bff',
                        background: '#000',
                        transition: 'all 0.3s ease'
                    }}
                >
                    {/* Loading indicator for iframe */}
                    {!iframeLoaded && !iframeError && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            fontSize: '14px',
                            textAlign: 'center',
                            zIndex: 10002
                        }}>
                            <div className="loading-spinner-large" style={{ marginBottom: '10px' }}></div>
                            Loading iframe player...
                            {connectionStatus === 'offline' && (
                                <div style={{ marginTop: '10px', color: '#ffc107', fontSize: '12px' }}>
                                    📶 Offline - Player may not load
                                </div>
                            )}
                        </div>
                    )}

                    {/* Error fallback for iframe */}
                    {iframeError && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            fontSize: '14px',
                            textAlign: 'center',
                            zIndex: 10002,
                            padding: '20px'
                        }}>
                            <div style={{ marginBottom: '10px', fontSize: '24px' }}>⚠️</div>
                            <div style={{ marginBottom: '10px' }}>Failed to load iframe player</div>
                            {connectionStatus === 'offline' && (
                                <div style={{ marginBottom: '10px', color: '#ffc107', fontSize: '12px' }}>
                                    📶 You appear to be offline
                                </div>
                            )}
                            <button
                                onClick={() => {
                                    setIframeError(false);
                                    setIframeLoaded(false);
                                    // Try audio fallback
                                    const { audioUrls } = getAudioSources(station);
                                    if (audioUrls.length > 0) {
                                        setPlayerType('audio');
                                        setEmbedUrl(null);
                                        loadAudioWithFallbacks(audioUrls, 0);
                                        //  toast.info('🔄 Switched to audio player');
                                    }
                                }}
                                style={{
                                    background: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Try Audio Player
                            </button>
                        </div>
                    )}

                    <iframe
                        src={embedUrl}
                        width="100%"
                        height={isExpanded ? "450px" : "100%"}
                        frameBorder="0"
                        allow="autoplay; encrypted-media; microphone"
                        allowFullScreen
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                        title={`${station.title} - Iono.fm Player`}
                        loading="lazy"
                        referrerPolicy="origin"
                        onLoad={() => {
                            console.log('📺 Iframe loaded successfully');
                            setIframeLoaded(true);
                            setIframeError(false);
                        }}
                        onError={(e) => {
                            console.log('❌ Iframe failed to load:', e);
                            setIframeError(true);
                            setIframeLoaded(false);
                        }}
                        style={{
                            border: 'none',
                            borderRadius: '12px',
                            maxWidth: '100%',
                            minHeight: isExpanded ? '450px' : '220px',
                            background: '#000',
                            opacity: iframeError ? 0.3 : 1,
                            transition: 'opacity 0.3s ease'
                        }}
                    ></iframe>

                    {/* Iframe Player Indicator */}
                    {!isExpanded && (
                        <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            background: iframeError ? 'rgba(220,53,69,0.9)' : !iframeLoaded ? 'rgba(255,193,7,0.9)' : 'rgba(0,123,255,0.9)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '16px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            zIndex: 10001,
                            transition: 'background 0.3s ease'
                        }}>
                            {iframeError ? '⚠️ FAILED' : !iframeLoaded ? '⏳ LOADING' : '📺 LIVE RADIO'}
                        </div>
                    )}
                </div>
            )}

            <div className={`floating-radio-player ${isExpanded ? 'expanded' : 'collapsed'} theme-bg-color`}>
                <div className="player-content">
                    {/* Minimized View */}
                    {!isExpanded && (
                        <div className="player-mini">
                            <div className="station-info-mini">
                                <div className="station-image-mini">
                                    <img
                                        src={station.image_URL || station.player_image_URL || '/default-radio-station.png'}
                                        alt={station.title}
                                        className="mini-image"
                                    />
                                    <div className="live-badge-mini">
                                        <FaWifi className="live-icon-mini" />
                                    </div>
                                </div>

                                <div className="station-details-mini">
                                    <h4 className="station-title-mini theme-text-color">
                                        {station.title}
                                    </h4>
                                    <p className="program-info-mini theme-text-color opacity-75">
                                        {currentProgram ? currentProgram.name : 'Live Radio'}
                                        {playerType === 'embed' && (
                                            <span className="player-type-badge" style={{
                                                background: '#007bff',
                                                color: 'white',
                                                padding: '2px 6px',
                                                borderRadius: '12px',
                                                fontSize: '10px',
                                                marginLeft: '8px'
                                            }}>
                                                📺 IFRAME PLAYER
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className="player-controls-mini">
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <button
                                        className="control-btn play-pause-btn theme-text-color"
                                        onClick={togglePlayPause}
                                        disabled={isLoading}
                                        title={isPlaying ? 'Pause' : 'Play'}
                                    >
                                        {isLoading ? (
                                            <div className="loading-spinner"></div>
                                        ) : isPlaying ? (
                                            <FaPause />
                                        ) : (
                                            <FaPlay />
                                        )}
                                    </button>
                                    
                                    {/* Autoplay Warning Badge */}
                                    {(showAutoplayWarning || autoplayBlocked) && !hasUserInteracted && (
                                        <div className="autoplay-warning-mini" style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            background: '#ffc107',
                                            color: '#000',
                                            borderRadius: '12px',
                                            padding: '2px 6px',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            zIndex: 1000,
                                            animation: 'autoplayPulse 2s infinite'
                                        }}>
                                            CLICK TO PLAY
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="control-btn next-btn theme-text-color"
                                    onClick={handleNextButtonClick}
                                    title="Next stations"
                                >
                                    <MdSkipNext />
                                </button>

                                {playerType === 'audio' && (
                                    <button
                                        className="control-btn volume-btn theme-text-color"
                                        onClick={toggleMute}
                                        title={isMuted ? 'Unmute' : 'Mute'}
                                    >
                                        {isMuted || volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                                    </button>
                                )}
                                
                                {/* Audio Fallback Button for Embed Players */}
                                {playerType === 'embed' && availableUrls && availableUrls.length > 0 && (
                                    <button
                                        className="control-btn audio-fallback-btn theme-text-color"
                                        onClick={() => {
                                            console.log('🔄 User requested switch to audio fallback');
                                            setPlayerType('audio');
                                            setEmbedUrl(null);
                                            setIframeLoaded(false);
                                            setIframeError(false);
                                            loadAudioWithFallbacks(availableUrls, 0);
                                        }}
                                        title="Switch to Audio Player"
                                        style={{ fontSize: '12px' }}
                                    >
                                        🎵
                                    </button>
                                )}

                                <button
                                    className="control-btn expand-btn theme-text-color"
                                    onClick={toggleExpanded}
                                    title="Expand player"
                                >
                                    <FaExpand />
                                </button>

                                <button
                                    className="control-btn close-btn theme-text-color"
                                    onClick={onClose}
                                    title="Close player"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Expanded View */}
                    {isExpanded && (
                        <div className="player-expanded">
                            <div className="player-header">
                                <div className="station-info-expanded">
                                    <div className="station-image-expanded">
                                        <img
                                            src={station.image_URL || station.player_image_URL || '/default-radio-station.png'}
                                            alt={station.title}
                                            className="expanded-image"
                                        />
                                        <div className="live-badge-expanded">
                                            <FaWifi className="live-icon-expanded" />
                                            <span>LIVE</span>
                                        </div>
                                    </div>

                                    <div className="station-details-expanded">
                                        <Link
                                            to={navigateTranslateChecker(`/music/radio/${station.slug}`)}
                                            className="station-title-link"
                                        >
                                            <h3 className="station-title-expanded theme-text-color">
                                                {station.title}
                                            </h3>
                                        </Link>

                                        <p className="station-description-expanded theme-text-color opacity-75">
                                            {station.description}
                                        </p>

                                        {currentProgram && (
                                            <div className="current-program">
                                                <h4 className="program-title theme-text-color-button">
                                                    Now Playing: {currentProgram.name}
                                                </h4>
                                                <p className="program-time theme-text-color opacity-50">
                                                    {currentProgram.start_time_formatted} - {currentProgram.end_time_formatted}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="player-actions-expanded">
                                    <button
                                        className="action-btn epg-btn-expanded theme-text-color"
                                        onClick={handleEPGClick}
                                        title="Program Guide (EPG)"
                                    >
                                        <FaList />
                                    </button>

                                    <button
                                        className="action-btn next-btn-expanded theme-text-color"
                                        onClick={handleNextButtonClick}
                                        title="Next stations"
                                    >
                                        <MdSkipNext />
                                    </button>

                                    <button
                                        className={`action-btn like-btn-expanded ${isLiked ? 'active' : ''} theme-text-color`}
                                        onClick={handleLikeToggle}
                                        title={isLiked ? 'Remove from favorites' : 'Add to favorites'}
                                    >
                                        {isLiked ? <FaHeart /> : <FaRegHeart />}
                                    </button>

                                    <button
                                        className="action-btn share-btn-expanded theme-text-color"
                                        onClick={handleShare}
                                        title="Share station"
                                    >
                                        <FaShareAlt />
                                    </button>

                                    <button
                                        className="action-btn collapse-btn theme-text-color"
                                        onClick={toggleExpanded}
                                        title="Collapse player"
                                    >
                                        <FaCompress />
                                    </button>

                                    <button
                                        className="action-btn close-btn-expanded theme-text-color"
                                        onClick={onClose}
                                        title="Close player"
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                            </div>

                            <div className="player-controls-expanded">
                                <div className="main-controls">
                                    <button
                                        className="control-btn-large play-pause-btn-large theme-button-bg-color theme-button-text-color"
                                        onClick={togglePlayPause}
                                        disabled={isLoading}
                                        title={isPlaying ? 'Pause' : 'Play'}
                                    >
                                        {isLoading ? (
                                            <div className="loading-spinner-large"></div>
                                        ) : isPlaying ? (
                                            <FaPause />
                                        ) : (
                                            <FaPlay />
                                        )}
                                    </button>
                                    
                                    {/* Autoplay instruction for expanded view */}
                                    {(showAutoplayWarning || autoplayBlocked) && !hasUserInteracted && (
                                        <div className="autoplay-instruction" style={{
                                            textAlign: 'center',
                                            marginTop: '10px',
                                            padding: '8px 12px',
                                            background: 'rgba(255, 193, 7, 0.1)',
                                            border: '1px solid #ffc107',
                                            borderRadius: '8px',
                                            fontSize: '14px'
                                        }}>
                                            <span style={{ color: '#ffc107' }}>🔒</span>
                                            <span className="theme-text-color" style={{ marginLeft: '8px' }}>
                                                Click the play button to start audio (browser autoplay restriction)
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Volume Controls - Only show for audio player */}
                                {playerType === 'audio' && (
                                    <div className="volume-controls">
                                        <button
                                            className="volume-btn-expanded theme-text-color"
                                            onClick={toggleMute}
                                            title={isMuted ? 'Unmute' : 'Mute'}
                                        >
                                            {isMuted || volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                                        </button>

                                        <input
                                            type="range"
                                            min="0"
                                            max="2"
                                            step="0.05"
                                            value={isMuted ? 0 : volume}
                                            onChange={handleVolumeChange}
                                            className="volume-slider"
                                        />

                                        <span className="volume-text theme-text-color opacity-75">
                                            {Math.round((isMuted ? 0 : volume) * 100)}%
                                        </span>
                                    </div>
                                )}
                                
                                {/* Embed Player Controls */}
                                {playerType === 'embed' && (
                                    <div className="embed-player-controls" style={{
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        gap: '10px',
                                        padding: '10px'
                                    }}>
                                        <span className="theme-text-color" style={{ fontSize: '14px' }}>
                                            📺 Use embedded player controls
                                        </span>
                                        {availableUrls && availableUrls.length > 0 && (
                                            <button
                                                onClick={() => {
                                                    console.log('🔄 Switching from embed to audio player');
                                                    setPlayerType('audio');
                                                    setEmbedUrl(null);
                                                    setIframeLoaded(false);
                                                    setIframeError(false);
                                                    setIsPlaying(false);
                                                    loadAudioWithFallbacks(availableUrls, 0);
                                                }}
                                                className="retry-btn-player theme-button-bg-color theme-button-text-color"
                                                style={{ padding: '8px 16px', fontSize: '12px' }}
                                            >
                                                🎵 Switch to Audio Player
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Error handling - iframe specific errors */}
                            {playerType === 'embed' && iframeError && (
                                <div className="error-message-player theme-text-color">
                                    <p>⚠️ Iframe player failed to load</p>
                                    {connectionStatus === 'offline' && (
                                        <p style={{ color: '#ffc107', fontSize: '14px', margin: '5px 0' }}>
                                            📶 Network connection appears to be offline
                                        </p>
                                    )}
                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                                        <button
                                            onClick={() => {
                                                setIframeError(false);
                                                setIframeLoaded(false);
                                                // Force iframe reload by changing src
                                                const currentUrl = embedUrl;
                                                setEmbedUrl(null);
                                                setTimeout(() => setEmbedUrl(currentUrl), 100);
                                                //  toast.info('🔄 Reloading iframe player...');
                                            }}
                                            className="retry-btn-player theme-button-bg-color theme-button-text-color"
                                        >
                                            🔄 Retry Iframe
                                        </button>
                                        <button
                                            onClick={() => {
                                                // Switch to audio fallback
                                                const { audioUrls } = getAudioSources(station);
                                                if (audioUrls.length > 0) {
                                                    setPlayerType('audio');
                                                    setEmbedUrl(null);
                                                    setIframeError(false);
                                                    setIframeLoaded(false);
                                                    loadAudioWithFallbacks(audioUrls, 0);
                                                    //  toast.info('🎵 Switched to audio player');
                                                } else {
                                                    //  toast.error('No audio fallback available');
                                                }
                                            }}
                                            className="retry-btn-player theme-button-bg-color theme-button-text-color"
                                        >
                                            🎵 Try Audio
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* General error handling */}
                            {error && playerType !== 'embed' && !autoplayBlocked && (
                                <div className="error-message-player theme-text-color">
                                    <p>{error}</p>
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="retry-btn-player theme-button-bg-color theme-button-text-color"
                                    >
                                        Reload Page
                                    </button>
                                </div>
                            )}
                            
                            {/* Autoplay blocked message */}
                            {autoplayBlocked && !hasUserInteracted && (
                                <div className="autoplay-blocked-message" style={{
                                    textAlign: 'center',
                                    padding: '20px',
                                    background: 'rgba(255, 193, 7, 0.1)',
                                    border: '1px solid #ffc107',
                                    borderRadius: '12px',
                                    margin: '15px 0'
                                }}>
                                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>🔒</div>
                                    <h4 className="theme-text-color" style={{ marginBottom: '8px' }}>
                                        Browser Autoplay Restricted
                                    </h4>
                                    <p className="theme-text-color opacity-75" style={{ fontSize: '14px', marginBottom: '15px' }}>
                                        Modern browsers require user interaction before audio can play.
                                        Click the play button above to start listening!
                                    </p>
                                    <button
                                        onClick={togglePlayPause}
                                        className="retry-btn-player theme-button-bg-color theme-button-text-color"
                                        style={{
                                            padding: '12px 24px',
                                            fontSize: '16px',
                                            borderRadius: '25px'
                                        }}
                                    >
                                        <FaPlay style={{ marginRight: '8px' }} />
                                        Start Playing Now
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Audio Visualizer */}
                {isPlaying && !error && (
                    <div className="audio-visualizer">
                        <div className="visualizer-bar bar-1"></div>
                        <div className="visualizer-bar bar-2"></div>
                        <div className="visualizer-bar bar-3"></div>
                        <div className="visualizer-bar bar-4"></div>
                        <div className="visualizer-bar bar-5"></div>
                    </div>
                )}
            </div>

            {/* EPG Modal */}
            {showEPGModal && (
                <RadioEPGModal
                    station={station}
                    onClose={() => dispatch(toggleEPGModalAction(false))}
                />
            )}

            {/* Enhanced Access Modal */}
            {showAccessModal && (
                <div className="access-modal-overlay">
                    <div className="access-modal theme-bg-color">
                        <div className="access-modal-header">
                            <FaLock className="lock-icon" />
                            <h3 className="theme-text-color">Premium Content</h3>
                        </div>

                        <div className="access-modal-body">
                            <div className="station-preview">
                                <img
                                    src={station?.image_URL || station?.player_image_URL || '/default-radio-station.png'}
                                    alt={station?.title}
                                    className="station-preview-image"
                                />
                                <h4 className="theme-text-color">{station?.title}</h4>
                            </div>

                            <p className="theme-text-color">
                                This radio station requires a premium subscription to access.
                            </p>
                            <p className="theme-text-color opacity-75">
                                Subscribe now to enjoy unlimited access to all premium radio stations.
                            </p>
                        </div>

                        <div className="access-modal-actions">
                            <button
                                className="access-btn subscribe-btn theme-button-bg-color theme-button-text-color"
                                onClick={handleAccessDenied}
                            >
                                <FaCreditCard />
                                Subscribe Now
                            </button>

                            <button
                                className="access-btn cancel-btn theme-text-color"
                                onClick={() => setShowAccessModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Next Station Panel */}
            {showNextPanel && (
                <NextStationPanel
                    currentStation={station}
                    onClose={() => setShowNextPanel(false)}
                    onStationSelect={handleNextStationFromPanel}
                />
            )}
        </>
    );
};

export default FloatingRadioPlayer;